import { useCallback, useEffect, useRef, useState } from "react";

export type SampleId = string;

export type PlayOptions = {
  /** Duración del fragmento original en segundos. */
  duration: number;
  /** Segundos extra antes del start. Se clampa a 0 si causa negativos. */
  paddingBefore?: number;
  /** Segundos extra después del end. */
  paddingAfter?: number;
};

export type SamplePlayerState = {
  /** ID del sample que se está reproduciendo, o null. */
  playingId: SampleId | null;
  /** Progreso 0..1 del sample actual. 0 si nada suena. */
  progress: number;
};

export type SamplePlayerControls = {
  /** Reproduce un sample. Si ya hay uno sonando, lo detiene primero. */
  play: (id: SampleId, startTime: number, opts: PlayOptions) => Promise<void>;
  /** Detiene cualquier sample en curso. */
  stop: () => void;
};

/**
 * Orquesta un <audio> ref para reproducir samples breves.
 *
 * Garantías:
 *   - Un solo sample suena a la vez.
 *   - Auto-stop al cumplir duration + padding.
 *   - Cleanup al desmontar el componente.
 *   - Re-clicks reinician desde el principio.
 *
 * Padding:
 *   - paddingBefore retrocede el cabezal antes del start (capped a 0).
 *   - paddingAfter extiende la duración total.
 *   - El progreso 0..1 se calcula sobre la duración total con padding.
 */
export function useSamplePlayer(
  audioRef: React.RefObject<HTMLAudioElement | null>,
): [SamplePlayerState, SamplePlayerControls] {
  const [playingId, setPlayingId] = useState<SampleId | null>(null);
  const [progress, setProgress] = useState(0);

  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    if (stopTimerRef.current !== null) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    if (progressTimerRef.current !== null) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    const a = audioRef.current;
    if (a && !a.paused) a.pause();
    clearTimers();
    setPlayingId(null);
    setProgress(0);
  }, [audioRef, clearTimers]);

  const play = useCallback(
    async (id: SampleId, startTime: number, opts: PlayOptions) => {
      const a = audioRef.current;
      if (!a) return;

      const before = Math.max(0, opts.paddingBefore ?? 0);
      const after = Math.max(0, opts.paddingAfter ?? 0);
      const effectiveStart = Math.max(0, startTime - before);
      const effectiveDuration = opts.duration + before + after;

      clearTimers();
      a.pause();

      // Si webkit no terminó de leer la metadata del WAV (readyState < 1),
      // forzar load() y esperar a que llegue al menos a HAVE_METADATA.
      if (a.readyState < 1 || !Number.isFinite(a.duration)) {
        a.load();
        await waitForMetadata(a);
      }

      // Validar que el seek tenga sentido.
      const safeStart = Number.isFinite(a.duration)
        ? Math.min(effectiveStart, Math.max(0, a.duration - 0.1))
        : effectiveStart;

      // Seek y esperar a que el navegador complete el posicionamiento
      // antes de arrancar la reproducción. Sin esto, play() puede
      // empezar desde la posición anterior y luego saltar, o resolver
      // antes de que el buffer en la nueva posición esté listo,
      // dessincronizando el indicador de progreso del audio real.
      a.currentTime = safeStart;
      await waitForSeeked(a);

      try {
        await a.play();
      } catch {
        return;
      }

      // Esperar a que el audio realmente esté produciendo output antes
      // de arrancar el contador de progreso. El evento `playing` se
      // dispara cuando el navegador está efectivamente reproduciendo
      // (post-buffer, post-seek).
      await waitForPlaying(a);

      setPlayingId(id);
      setProgress(0);

      stopTimerRef.current = setTimeout(() => {
        stop();
      }, effectiveDuration * 1000);

      const startedAt = performance.now();
      progressTimerRef.current = setInterval(() => {
        const elapsedMs = performance.now() - startedAt;
        const ratio = Math.min(elapsedMs / (effectiveDuration * 1000), 1);
        setProgress(ratio);
      }, 50);
    },
    [audioRef, clearTimers, stop],
  );

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      clearTimers();
      const a = audioRef.current;
      if (a && !a.paused) a.pause();
    };
  }, [audioRef, clearTimers]);

  return [{ playingId, progress }, { play, stop }];
}

/**
 * Espera a que el <audio> tenga al menos metadata cargada (readyState >= 1),
 * con un timeout de 10s para no colgar la UI si algo se rompió.
 */
function waitForMetadata(audio: HTMLAudioElement, timeoutMs = 10000): Promise<void> {
  return new Promise((resolve) => {
    if (audio.readyState >= 1 && Number.isFinite(audio.duration)) {
      resolve();
      return;
    }

    let resolved = false;
    const cleanup = () => {
      audio.removeEventListener("loadedmetadata", onMetadata);
      audio.removeEventListener("canplay", onMetadata);
      audio.removeEventListener("error", onError);
      clearTimeout(timeout);
    };

    const onMetadata = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve();
    };

    const onError = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve();
    };

    const timeout = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve();
    }, timeoutMs);

    audio.addEventListener("loadedmetadata", onMetadata);
    audio.addEventListener("canplay", onMetadata);
    audio.addEventListener("error", onError);
  });
}

/**
 * Espera a que el navegador complete el seek iniciado por `currentTime = X`.
 * El evento `seeked` se dispara cuando el cabezal ya está en la posición
 * correcta y el buffer está listo para reproducir desde ahí.
 *
 * Timeout de 5s: si el seek nunca completa (archivo corrupto, red lenta),
 * seguimos adelante para no colgar la UI.
 */
function waitForSeeked(audio: HTMLAudioElement, timeoutMs = 5000): Promise<void> {
  return new Promise((resolve) => {
    let resolved = false;
    const cleanup = () => {
      audio.removeEventListener("seeked", onSeeked);
      audio.removeEventListener("error", onError);
      clearTimeout(timer);
    };

    const onSeeked = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve();
    };

    const onError = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve();
    };

    const timer = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve();
    }, timeoutMs);

    audio.addEventListener("seeked", onSeeked, { once: true });
    audio.addEventListener("error", onError, { once: true });
  });
}

/**
 * Espera a que el audio esté efectivamente reproduciendo. El evento
 * `playing` se dispara cuando el navegador está produciendo output después
 * de un play(), seek, o buffer underrun.
 *
 * Timeout de 3s: si play() ya resolvió, `playing` debería dispararse
 * casi inmediatamente. El timeout evita colgar si algo sale mal.
 */
function waitForPlaying(audio: HTMLAudioElement, timeoutMs = 3000): Promise<void> {
  return new Promise((resolve) => {
    if (!audio.paused) {
      resolve();
      return;
    }

    let resolved = false;
    const cleanup = () => {
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("error", onError);
      clearTimeout(timer);
    };

    const onPlaying = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve();
    };

    const onError = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve();
    };

    const timer = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve();
    }, timeoutMs);

    audio.addEventListener("playing", onPlaying, { once: true });
    audio.addEventListener("error", onError, { once: true });
  });
}
